import { useMemo } from 'react';
import type {
  InvoiceFigure,
  InvoiceFigures,
  InvoiceTotalsSummary,
  ProcessingResult,
} from '../../../data/receiving';
import { INVOICE_FIGURES, summarizeInvoiceTotals } from '../../../data/receiving';
import { fullCurrencyFormatter } from '../../../helpers/format-currency';
import { pluralize } from '../../../helpers/pluralize';

// Whether the owner is about to send an incomplete result to LiquorPOS. Only
// the files that bear on that question are drawn, one panel each:
//
//   disagrees   the comparison, headed by its own column names. Two figures
//               agree legibly only when they sit in one column in the same
//               monospace, directly above each other (DESIGN.md, the
//               JetBrains and Mono Cell rules).
//   unverified  one line. There is nothing to compare, so a comparison table
//               would be three rows of chrome around a single number.
//   matches     nothing. We ran the check; reporting a passed check on every
//               delivery would train the owner to stop reading this region,
//               and the red row needs them to still be reading it.
//
// The unverified line survives that cut on an asymmetry worth keeping in mind
// before deleting it. For a file that printed totals, our figure adds nothing
// — we already compared it. For the formats that print none (LDF, RNDC-OK,
// DSD, BEES, generic CSV, most of the CSV volume), automatic detection is
// permanently impossible, so an owner's own glance at the number is the entire
// defence. That is the blind spot ORD-392 exists to close: twice a reader has
// shipped wrong costs with nothing to flag them (MRs !4229 and !4365).
//
// What it still must not become is a "we couldn't verify this one" caveat. It
// would fire on the same vendors every single day, and a permanent grey
// warning trains owners to ignore the region just as surely.
//
// Everything here is drawn with four treatments and no more, because an
// earlier revision reached ten and read as noise: an uppercase eyebrow once,
// WORDS for anything that names something, FIGURES for anything measured, and
// those same two in rose for the one row that disagrees. A fifth is a
// regression, not a refinement.
const WORDS = 'text-sm font-normal text-gray-500';
const FIGURES = 'text-right font-mono text-sm tabular-nums text-gray-900';

const HEADINGS: Record<InvoiceFigure, string> = {
  amount: 'Amount',
  cases: 'Cases',
  units: 'Units',
  lines: 'Lines',
};

const count = (n: number, noun: string) => `${n.toLocaleString('en-US')} ${pluralize(n, noun)}`;

// Money formats as money; the rest are plain counts. A signed difference reads
// from the invoice's side, so "+" means the invoice claims more than we found.
function cell(figure: InvoiceFigure, value: number, signed = false): string {
  const sign = signed && value > 0 ? '+' : '';
  if (figure === 'amount') {
    return `${sign}${fullCurrencyFormatter(value)}`;
  }

  return `${sign}${value.toLocaleString('en-US')}`;
}

// A column is a comparison, so only the figures this invoice actually printed
// earn one. This is what deletes the line count: no vendor surveyed prints a
// line total, so printed.lines is null by construction and the rule drops the
// column without naming it. Nothing has to render an em dash to stand in for a
// figure that was never coming, and a vendor printing only an amount gets a
// one-column panel rather than three columns of placeholder.
const comparedFigures = (summary: InvoiceTotalsSummary): InvoiceFigure[] =>
  INVOICE_FIGURES.filter((figure) => summary.printed[figure] != null);

function Row({
  label,
  testId,
  figures,
  columns,
}: {
  label: string;
  testId: string;
  figures: InvoiceFigures;
  columns: InvoiceFigure[];
}) {
  return (
    <tr data-testid={`invoice-total-row-${testId}`}>
      <th scope="row" className={`py-1 pr-4 text-left whitespace-nowrap ${WORDS}`}>
        {label}
      </th>
      {columns.map((figure) => (
        <td key={figure} className={`py-1 pl-4 ${FIGURES}`}>
          {cell(figure, figures[figure] as number)}
        </td>
      ))}
    </tr>
  );
}

// The answer to the question, and the only coloured thing in the panel. It
// carries no background tint: a tinted number would be a badge, and the one
// badge component the system allows (StatusCell, danger/soft) pairs #dc2626 on
// #fee2e2 for 3.97:1, under the AA floor PRODUCT.md makes binding for older
// eyes on washed-out LCDs. Rose text alone clears 9:1 and costs no extra
// vocabulary. A figure that agrees is left blank rather than shown as zero;
// the two rows above already stand as the evidence that it was checked.
function DifferenceRow({
  summary,
  columns,
}: {
  summary: InvoiceTotalsSummary;
  columns: InvoiceFigure[];
}) {
  return (
    <tr data-testid="invoice-total-row-difference">
      <th
        scope="row"
        className="py-1 pr-4 text-left text-sm font-normal whitespace-nowrap text-rose-800"
      >
        Difference
      </th>
      {columns.map((figure) => {
        const delta = summary.difference[figure];

        return (
          <td
            key={figure}
            data-differs={delta == null ? undefined : 'true'}
            className={`py-1 pl-6 text-right font-mono text-sm tabular-nums ${
              delta == null ? '' : 'font-semibold text-rose-800'
            }`}
          >
            {delta == null ? '' : cell(figure, delta, true)}
          </td>
        );
      })}
    </tr>
  );
}

function Comparison({ summary }: { summary: InvoiceTotalsSummary }) {
  const columns = comparedFigures(summary);

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <td className="border-b border-gray-200" />
            {columns.map((figure) => (
              <th
                key={figure}
                scope="col"
                className={`border-b border-gray-200 pb-1.5 pl-4 text-right ${WORDS}`}
              >
                {HEADINGS[figure]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <Row label="We read" testId="read" figures={summary.read} columns={columns} />
          <Row label="Invoice says" testId="printed" figures={summary.printed} columns={columns} />
          <DifferenceRow summary={summary} columns={columns} />
        </tbody>
      </table>
    </div>
  );
}

// Nothing to compare against, so no table and no caveat — just the number the
// owner can hold against the paper invoice, opening on the same two words the
// comparison's first row uses so both panels speak with one voice.
function UnverifiedRead({ summary }: { summary: InvoiceTotalsSummary }) {
  const { amount, lines, cases } = summary.read;

  return (
    <p data-testid="invoice-total-unverified" className={WORDS}>
      {'We read '}
      <span className="font-mono text-gray-900">{fullCurrencyFormatter(amount ?? 0)}</span>
      {` from ${count(lines ?? 0, 'line')}, ${count(cases ?? 0, 'case')}.`}
    </p>
  );
}

// Inside the panel for the file it describes, so "this total" needs no
// filename to disambiguate it and the sentence stays one plain line.
function UnreadRowsNote({ rows }: { rows: number }) {
  return (
    <p data-testid="invoice-total-unread-note" className={`mt-2 ${WORDS}`}>
      {`${count(rows, 'row')} couldn't be read, so ${
        rows === 1 ? "it's" : "they're"
      } missing from this total.`}
    </p>
  );
}

function FilePanel({
  summary,
  showFilename,
}: {
  summary: InvoiceTotalsSummary;
  showFilename: boolean;
}) {
  return (
    <div className="min-w-0 rounded-md bg-gray-50 px-4 py-3">
      {/* A flat tinted region, not a card: DESIGN.md quarantines cards (shadow,
          border, its own affordances) to the marketing register. The tint is
          only here to say where one invoice ends and the next begins. */}
      {showFilename && (
        <p className="mb-2 truncate font-mono text-sm text-gray-900">{summary.filename}</p>
      )}
      {summary.verdict === 'differs' ? (
        <>
          <Comparison summary={summary} />
          {summary.unreadRows > 0 && <UnreadRowsNote rows={summary.unreadRows} />}
        </>
      ) : (
        <UnverifiedRead summary={summary} />
      )}
    </div>
  );
}

export function InvoiceTotals({ result }: { result: ProcessingResult }) {
  const summaries = useMemo(() => summarizeInvoiceTotals(result), [result]);
  // A file that agrees with its printed totals is drawn as nothing at all.
  // Comparisons lead, and the one-line unverified panels trail: a five-row
  // panel beside a one-line one leaves the row visibly ragged, and putting
  // the loud ones first is the reading order anyway.
  const shown = useMemo(
    () => [
      ...summaries.filter((s) => s.verdict === 'differs'),
      ...summaries.filter((s) => s.verdict === 'unverified'),
    ],
    [summaries]
  );

  // Name the file whenever the session carried more than one — including when
  // the others were the silent, agreeing kind. A surviving panel still has to
  // say which of the owner's files it is about.
  const showFilename = summaries.length > 1;

  // Nothing worth saying: every file agreed, or the session predates ORD-392
  // and carries no totals at all.
  if (shown.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="invoice-totals"
      className="flex flex-col gap-2 border-t border-gray-100 px-4 py-3"
    >
      <span className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
        Invoice totals
      </span>
      {/* One panel per file, laid across the width rather than stacked in a
          column against a wide empty margin. Each panel heads its own columns:
          a single shared header sat too far from the second file's figures to
          name them. */}
      <div className="grid w-full gap-3 [grid-template-columns:repeat(auto-fill,minmax(min(23rem,100%),25rem))]">
        {shown.map((summary) => (
          <FilePanel key={summary.filename} summary={summary} showFilename={showFilename} />
        ))}
      </div>
    </div>
  );
}
